import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useIntl } from 'react-intl';
import { IPlanExercise, IPlanGroup, ISetConfiguration } from '../types/workout/plan-state';

interface SetConfigDialogProps {
  visible: boolean;
  onHide: () => void;
  selectedExercise: IPlanExercise | null;
  selectedGroup: IPlanGroup | null;
  toggleSetConfiguration: (groupId: string | number, exerciseId: string | number, enable: boolean) => void;
  updateSetConfiguration: (
    groupId: string | number,
    exerciseId: string | number,
    setIndex: number,
    property: string,
    value: string
  ) => void;
}

type ColumnKey = 'repetitions' | 'weight' | 'restInterval' | 'tempo' | 'notes' | 'distance' | 'time' | 'difficulty';

const BASE_COLUMNS: ColumnKey[] = ['repetitions', 'weight', 'restInterval'];
const OPTIONAL_COLUMNS: ColumnKey[] = ['tempo', 'notes', 'distance', 'time', 'difficulty'];

const COLUMN_LABELS: Record<ColumnKey, { key: string; default: string; width: number; placeholder: string }> = {
  repetitions: { key: 'session.reps', default: 'Reps', width: 80, placeholder: '10' },
  weight: { key: 'session.weight', default: 'Peso (kg)', width: 90, placeholder: '50' },
  restInterval: { key: 'setConfig.col.rest', default: 'Descanso (s)', width: 100, placeholder: '60' },
  tempo: { key: 'setConfig.col.tempo', default: 'Tempo', width: 80, placeholder: '2-0-2-0' },
  notes: { key: 'setConfig.col.notes', default: 'Notas', width: 140, placeholder: '' },
  distance: { key: 'setConfig.col.distance', default: 'Distancia (m)', width: 100, placeholder: '100' },
  time: { key: 'setConfig.col.time', default: 'Tiempo (s)', width: 90, placeholder: '30' },
  difficulty: { key: 'setConfig.col.difficulty', default: 'Dificultad', width: 100, placeholder: 'Media' }
};

/**
 * v2 Set Config dialog — spreadsheet layout (design handoff §4.1).
 *
 * Behavior changes vs legacy:
 * - No "enable" checkbox: opening the dialog implies personalized mode. The
 *   underlying `setConfiguration[]` is initialized lazily on first open.
 * - Rows = sets, columns = props. Base columns always visible; optional
 *   columns toggle via chips.
 * - Bulk toolbar: copy set 1 to all, progressive +2.5kg, reset to parent defaults.
 * - Each optional/base column header carries a "copy down" mini-button.
 *
 * Schema unchanged — `ISetConfiguration[]` still drives persistence.
 */
export const SetConfigDialog: React.FC<SetConfigDialogProps> = ({
  visible,
  onHide,
  selectedExercise,
  selectedGroup,
  toggleSetConfiguration,
  updateSetConfiguration
}) => {
  const intl = useIntl();
  const [extraColumns, setExtraColumns] = useState<Set<ColumnKey>>(new Set());

  // Auto-enable setConfiguration on first open + keep row count in sync with
  // the parent exercise's `sets` value. If the user edits sets from 1 → 3 in
  // the plan editor, the dialog must show 3 rows next time it opens.
  useEffect(() => {
    if (!visible || !selectedExercise || !selectedGroup) return;
    const targetSets = parseInt(String(selectedExercise.sets), 10) || 3;
    const current = selectedExercise.setConfiguration;
    const needsInit = !current || current.length === 0;
    const needsResize = !!current && current.length !== targetSets;
    if (!needsInit && !needsResize) return;
    // Regenerate from scratch — toggleSetConfiguration(true) rebuilds the array
    // sized to exercise.sets. We pass `false` first only when the array
    // already exists, so the rebuild path always sees a clean slate.
    if (needsResize) {
      toggleSetConfiguration(selectedGroup.id, selectedExercise.id, false);
    }
    toggleSetConfiguration(selectedGroup.id, selectedExercise.id, true);
  }, [visible, selectedExercise, selectedGroup, toggleSetConfiguration]);

  // When a setConfiguration exists, derive which optional columns already
  // have data so the chips start in the right state.
  useEffect(() => {
    if (!selectedExercise?.setConfiguration) return;
    const next = new Set<ColumnKey>();
    for (const cfg of selectedExercise.setConfiguration) {
      for (const col of OPTIONAL_COLUMNS) {
        const v = (cfg as Record<string, unknown>)[col];
        if (v !== undefined && v !== null && String(v).trim().length > 0) {
          next.add(col);
        }
      }
    }
    setExtraColumns(next);
  }, [selectedExercise?.setConfiguration]);

  const visibleColumns = useMemo<ColumnKey[]>(() => {
    return [...BASE_COLUMNS, ...OPTIONAL_COLUMNS.filter((c) => extraColumns.has(c))];
  }, [extraColumns]);

  if (!selectedExercise || !selectedGroup) return null;

  const sets: ISetConfiguration[] = selectedExercise.setConfiguration ?? [];

  const labelOf = (key: ColumnKey) => {
    const cfg = COLUMN_LABELS[key];
    return intl.formatMessage({ id: cfg.key, defaultMessage: cfg.default });
  };

  const setCellValue = (rowIdx: number, col: ColumnKey, value: string) => {
    updateSetConfiguration(selectedGroup.id, selectedExercise.id, rowIdx, col, value);
  };

  const applyColumnToAll = (col: ColumnKey) => {
    if (sets.length === 0) return;
    const source = (sets[0] as Record<string, unknown>)[col];
    const value = source === undefined || source === null ? '' : String(source);
    sets.forEach((_, i) => {
      if (i === 0) return;
      setCellValue(i, col, value);
    });
  };

  const bulkCopyFirst = () => {
    if (sets.length === 0) return;
    sets.forEach((_, i) => {
      if (i === 0) return;
      visibleColumns.forEach((col) => {
        const source = (sets[0] as Record<string, unknown>)[col];
        const value = source === undefined || source === null ? '' : String(source);
        setCellValue(i, col, value);
      });
    });
  };

  const bulkProgressive = () => {
    const base = parseFloat((sets[0] as Record<string, unknown>)?.weight as string);
    if (!Number.isFinite(base)) return;
    sets.forEach((_, i) => {
      if (i === 0) return;
      const next = (base + i * 2.5).toString();
      setCellValue(i, 'weight', next);
    });
  };

  const bulkReset = () => {
    const fallback: Partial<Record<ColumnKey, string>> = {
      repetitions: selectedExercise.repetitions ?? '',
      weight: selectedExercise.weight ?? '',
      restInterval: (selectedExercise as unknown as Record<string, string>).restInterval ?? '',
      tempo: (selectedExercise as unknown as Record<string, string>).tempo ?? '',
      notes: (selectedExercise as unknown as Record<string, string>).notes ?? '',
      distance: (selectedExercise as unknown as Record<string, string>).distance ?? '',
      time: (selectedExercise as unknown as Record<string, string>).time ?? '',
      difficulty: (selectedExercise as unknown as Record<string, string>).difficulty ?? ''
    };
    sets.forEach((_, i) => {
      visibleColumns.forEach((col) => {
        setCellValue(i, col, fallback[col] ?? '');
      });
    });
  };

  const toggleColumn = (col: ColumnKey) => {
    setExtraColumns((prev) => {
      const next = new Set(prev);
      if (next.has(col)) {
        next.delete(col);
        // Clear cells of that column when hidden, so collapsed columns don't
        // keep stale data.
        sets.forEach((_, i) => setCellValue(i, col, ''));
      } else {
        next.add(col);
      }
      return next;
    });
  };

  return (
    <Dialog
      header={`${intl.formatMessage({ id: 'setConfig.title', defaultMessage: 'Configuración de Sets' })} · ${selectedExercise.exercise?.name ?? ''}`}
      visible={visible}
      style={{ width: '900px', maxWidth: '95vw' }}
      onHide={onHide}
      footer={
        <Button
          label={intl.formatMessage({ id: 'common.close', defaultMessage: 'Cerrar' })}
          icon="pi pi-times"
          onClick={onHide}
          className="p-button-text"
        />
      }
    >
      {/* Bulk toolbar */}
      <div className="et-setconfig__toolbar" role="toolbar">
        <Button
          label={intl.formatMessage({ id: 'setConfig.bulk.copyFirstToAll' })}
          icon="pi pi-copy"
          className="p-button-sm p-button-outlined"
          onClick={bulkCopyFirst}
          disabled={sets.length < 2}
        />
        <Button
          label={intl.formatMessage({ id: 'setConfig.bulk.progressive' })}
          icon="pi pi-arrow-up-right"
          className="p-button-sm p-button-outlined"
          onClick={bulkProgressive}
          disabled={sets.length < 2}
        />
        <Button
          label={intl.formatMessage({ id: 'setConfig.bulk.reset' })}
          icon="pi pi-refresh"
          className="p-button-sm p-button-text"
          onClick={bulkReset}
        />
        <span className="et-setconfig__divider" aria-hidden />
        <span className="et-setconfig__columns-label">{intl.formatMessage({ id: 'setConfig.columns.label' })}:</span>
        {OPTIONAL_COLUMNS.map((col) => {
          const active = extraColumns.has(col);
          return (
            <button
              key={col}
              type="button"
              onClick={() => toggleColumn(col)}
              className={`et-setconfig__col-chip${active ? ' is-active' : ''}`}
            >
              {labelOf(col)}
            </button>
          );
        })}
      </div>

      {/* Spreadsheet */}
      <div className="et-setconfig__sheet">
        <div className="et-setconfig__grid">
          {/* Header */}
          <div className="et-setconfig__row et-setconfig__row--head">
            <div className="et-setconfig__cell et-setconfig__cell--set">Set</div>
            {visibleColumns.map((col) => (
              <div
                key={col}
                className="et-setconfig__cell et-setconfig__cell--head"
                style={{ width: COLUMN_LABELS[col].width }}
              >
                <span>{labelOf(col)}</span>
                <button
                  type="button"
                  className="et-setconfig__apply-btn"
                  title={intl.formatMessage({ id: 'setConfig.column.applyToAll' })}
                  aria-label={intl.formatMessage({ id: 'setConfig.column.applyToAll' })}
                  onClick={() => applyColumnToAll(col)}
                  disabled={sets.length < 2}
                >
                  <i className="pi pi-arrow-down" style={{ fontSize: '0.7rem' }} />
                </button>
              </div>
            ))}
          </div>

          {/* Rows */}
          {sets.map((set, rowIdx) => (
            <div key={rowIdx} className="et-setconfig__row">
              <div className="et-setconfig__cell et-setconfig__cell--set">{set.setNumber ?? rowIdx + 1}</div>
              {visibleColumns.map((col) => {
                const raw = (set as Record<string, unknown>)[col];
                const value = raw === undefined || raw === null ? '' : String(raw);
                return (
                  <div key={col} className="et-setconfig__cell" style={{ width: COLUMN_LABELS[col].width }}>
                    <InputText
                      className="w-full p-inputtext-sm et-setconfig__input"
                      value={value}
                      placeholder={COLUMN_LABELS[col].placeholder}
                      onChange={(e) => setCellValue(rowIdx, col, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          ))}

          {sets.length === 0 && (
            <div className="et-setconfig__empty">
              {intl.formatMessage({
                id: 'setConfig.empty',
                defaultMessage: 'No hay sets configurados. Configurá el número de sets en el ejercicio padre.'
              })}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
